import React, { useEffect, useState } from "react";
import CardEmpty from "Utils/Cards/CardEmpty";
import { NotificationManager } from "react-notifications";
import AsyncSelect from "react-select/lib/Async";
import DatePicker from "react-datepicker";
import { AlmacenesInventarioApi } from "../api/AlmacenesInventarioApi";
import LoadMask from "../../../../../Utils/LoadMask/LoadMask";
import { AlmacenesTable } from "./AlmacenesTable/AlmacenesTable";
import {
    dateToDayStart,
    getErrorFromResponse,
} from "../../../../../../../utility/utils";
import moment from "moment";
import { FormReajuste } from "./FormReajuste";
import { ESTADOS_INV_ALMACEN } from "../../../../../../../utility/constants";
import { CierreInventarioForm } from "./CierreInventario/CierreTurnoForm";
import { useDebouncedAsyncSelect } from "../../../../../Utils/Hooks/useDebounce";
const refreshIcon = require("assets/img/icons/refresh.png");

export const ListadoAlmacenes = ({
    inventarioId,
    regresar,
    categoriaAcopio,
    nombreAcopio,
    verIngresosAlmacen,
    filtroPadrePT,
    onFiltroPadrePTChange
}) => {
    const [almacenes, setAlmacenes] = useState([]);

    const {
        getAlmacenesInventario,
        actualizarInventario,
        actualizarEstadoAlmacen,
        getPadresProductoTerminado,

        cargando,
    } = AlmacenesInventarioApi();

    const [fechaInicial, setFechaInicial] = useState(null);
    const [fechaFinal, setFechaFinal] = useState(null);

    const [estaCerrado, setEstaCerrado] = useState(false);

    const [openedModalCierreTurno, setOpenedModalCierreTurno] = useState(false);

    const GetAlmacenesInventario = async () => {
        try {
            const response = await getAlmacenesInventario(inventarioId);
            if (response) {
                setAlmacenes(response.almacenes);
                setFechaInicial(response.fecha_inicial_inventario_actual);

                // La fecha final que viene del backend es la fecha real + 1 día, para incluir el día seleccionado
                // Por eso, para mostrar la fecha al usuario, se le resta 1 día
                const fechaFinalResponse =
                    response.fecha_final_inventario_actual;
                const userFechaFinal = fechaFinalResponse
                    ? moment(fechaFinalResponse).subtract(1, "day")
                    : null;

                setFechaFinal(userFechaFinal);
                setEstaCerrado(response.esta_cerrado);
            }
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener almacenes disponibles",
            );
            NotificationManager.error(errorMsg, "Error", 3000);

            setAlmacenes([]);
        }
    };

    useEffect(() => {
        GetAlmacenesInventario();
    }, []);

    const GuardarInventario = async (_fechaFinal) => {
        try {
            // Si la fecha final es menor a la fecha inicial, mostrar error y no guardar
            if (
                _fechaFinal &&
                fechaInicial &&
                moment(_fechaFinal).isSameOrBefore(fechaInicial, "day")
            ) {
                NotificationManager.error(
                    "La fecha final debe ser posterior a la fecha inicial",
                    "Error",
                    3000,
                );
                GetAlmacenesInventario();
                return;
            }

            // La fecha final que se guarda en el backend es la fecha real + 1 día, para incluir el día seleccionado
            const realFechaFinal = _fechaFinal
                ? moment(_fechaFinal).add(1, "day")
                : null;

            const datos = {
                fecha_final: dateToDayStart(realFechaFinal),
            };
            await actualizarInventario(inventarioId, datos);

            await GetAlmacenesInventario();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al actualizar el inventario",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const VerificarAlmacenesPendientes = () => {
        // Si hay almacenes con estados pendientes, no permitir cerrar el inventario
        const hayPendientes = almacenes.some((almacen) =>
            [
                ESTADOS_INV_ALMACEN.PENDIENTE,
                ESTADOS_INV_ALMACEN.PENDIENTE_ANALISIS,
            ].includes(almacen.estado),
        );
        if (hayPendientes) {
            NotificationManager.warning(
                "No se puede cerrar el inventario. Hay almacenes con estados pendientes.",
                "Advertencia",
                3000,
            );
            return;
        }

        // Los ingresos sin análisis de laboratorio o sin liquidación se
        // guardarían sin sus kg secos; el backend valida lo mismo al cerrar
        const almacenesConDrcFaltante = almacenes
            .filter((almacen) => almacen.ingresos_periodo.drc_faltante)
            .map((almacen) => almacen.nombre_almacen);

        if (almacenesConDrcFaltante.length) {
            NotificationManager.warning(
                "No se puede cerrar el inventario. Hay ingresos sin análisis de " +
                    "laboratorio o sin liquidación (marcados en rojo) en: " +
                    almacenesConDrcFaltante.join(", ") +
                    ".",
                "Advertencia",
                3000,
            );
            return;
        }

        MostrarMensajeCierreInventario();
    };

    const MostrarMensajeCierreInventario = () => {
        let mensaje =
            "Al cerrar el inventario, no se podrán hacer más cambios en los almacenes.";
        mensaje += "Tampoco se podrá eliminar el inventario. ¿Desea continuar?";

        setOpenedModalCierreTurno(true);
    };

    const ActualizarEstadoAlmacen = async (
        almacenInventarioId,
        nuevoEstado,
    ) => {
        try {
            const data = {
                estado: nuevoEstado,
            };
            await actualizarEstadoAlmacen(almacenInventarioId, data);
            GetAlmacenesInventario();

            NotificationManager.success(
                "Estado del almacén actualizado correctamente",
                "Éxito",
                3000,
            );
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al actualizar el estado del almacén",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const [almacenInvReajuste, setAlmacenInvReajuste] = useState(null);

    const loadPadresProductoTerminado = useDebouncedAsyncSelect(
        function (search) {
            return getPadresProductoTerminado({ search: search });
        },
    );

    const [almacenesFiltradosPT, setAlmacenesFiltradosPT] = useState([]);

    useEffect(() => {
        if (filtroPadrePT) {
            const filtrados = almacenes.filter((almacen) =>
                almacen.almacen_padre_producto_terminado_id
                    ? almacen.almacen_padre_producto_terminado_id === filtroPadrePT.id
                    : false,
            );
            setAlmacenesFiltradosPT(filtrados);
        } else {
            setAlmacenesFiltradosPT(almacenes);
        }
    }, [filtroPadrePT, almacenes]);

    return (
        <div className="almacenes-inventario__tab-wrapper">
            <CardEmpty noShadow>
                <LoadMask loading={cargando} blur>
                    <div className="almacenes-inventario__table-header">
                        <div className="mr-auto formulario-fields-row">
                            <div className="formulario-field">
                                <label htmlFor="acopio" className="m-0">
                                    Acopio
                                </label>
                                <input
                                    type="text"
                                    id="acopio"
                                    className="form-control"
                                    value={nombreAcopio}
                                    disabled
                                />
                            </div>
                            <div className="formulario-field">
                                <label htmlFor="acopio" className="m-0 text-nowrap">
                                    Padre producto terminado
                                </label>
                                <AsyncSelect
                                    className="react-select-container"
                                    isSearchable
                                    isClearable
                                    defaultOptions
                                    loadOptions={loadPadresProductoTerminado}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nombre}
                                    value={filtroPadrePT}
                                    onChange={(selected) =>
                                        onFiltroPadrePTChange(selected)
                                    }
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <img
                                src={refreshIcon}
                                alt="Refrescar listado"
                                title="Refrescar listado"
                                className="action_img mb-1 mt-auto"
                                onClick={GetAlmacenesInventario}
                            />
                        </div>
                        <div
                            className="ml-auto formulario-fields-row"
                            style={{ gap: "1rem" }}
                        >
                            <div className="formulario-field">
                                <label htmlFor="fecha_inicial" className="m-0">
                                    Fecha inicial
                                </label>
                                <input
                                    type="text"
                                    id="fecha_inicial"
                                    className="form-control"
                                    value={
                                        fechaInicial
                                            ? moment(fechaInicial).format(
                                                  "DD/MM/YYYY",
                                              )
                                            : ""
                                    }
                                    disabled
                                />
                            </div>
                            <div className="formulario-field">
                                <label htmlFor="fecha_final" className="m-0">
                                    Fecha final
                                </label>
                                {estaCerrado ? (
                                    <input
                                        type="text"
                                        id="fecha_final"
                                        className="form-control"
                                        value={
                                            fechaFinal
                                                ? moment(fechaFinal).format(
                                                      "DD/MM/YYYY",
                                                  )
                                                : ""
                                        }
                                        disabled
                                    />
                                ) : (
                                    <DatePicker
                                        selected={fechaFinal}
                                        onChange={(date) => {
                                            if (!date) return;
                                            setFechaFinal(date);
                                            GuardarInventario(date);
                                        }}
                                        showPopperArrow={false}
                                        dateFormat="DD/MM/YYYY"
                                        placeholderText="Fecha final"
                                        disabled={estaCerrado}
                                        minDate={moment(fechaInicial)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <AlmacenesTable
                        almacenes={almacenesFiltradosPT}
                        estaCerrado={estaCerrado}
                        setAlmacenInvReajuste={setAlmacenInvReajuste}
                        ActualizarEstadoAlmacen={ActualizarEstadoAlmacen}
                        categoriaAcopio={categoriaAcopio}
                        verIngresosAlmacen={verIngresosAlmacen}
                    />

                    <div
                        className="d-flex justify-content-center my-4"
                        style={{ gap: "2rem" }}
                    >
                        <button
                            className="btn btn-outline-dark"
                            onClick={regresar}
                        >
                            Regresar
                        </button>

                        {!estaCerrado && (
                            <React.Fragment>
                                <button
                                    className="btn btn-secondary"
                                    onClick={VerificarAlmacenesPendientes}
                                >
                                    Cerrar inventario
                                </button>
                            </React.Fragment>
                        )}
                    </div>
                </LoadMask>
                <FormReajuste
                    almacenInvReajuste={almacenInvReajuste}
                    onClose={() => setAlmacenInvReajuste(null)}
                    onRefresh={GetAlmacenesInventario}
                />
            </CardEmpty>
            <CierreInventarioForm
                opened={openedModalCierreTurno}
                onClose={() => setOpenedModalCierreTurno(false)}
                inventarioId={inventarioId}
                fechaInicial={fechaInicial}
                fechaFinal={fechaFinal}
                GetAlmacenesInventario={GetAlmacenesInventario}
                onSuccess={regresar}
                categoriaAcopio={categoriaAcopio}
            />
        </div>
    );
};
